import React from 'react';
import { X } from 'lucide-react';

export default function HelpModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-2xl mx-4 p-6 rounded-lg shadow-lg overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-800">About This Risk Management Tool</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4 text-gray-700 text-sm leading-relaxed">
          <p>
            This application is designed to help you identify, evaluate, and monitor project risks through an
            intuitive interface that combines several proven risk-management techniques.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Interactive Risk Matrix:</strong> Visualize both <em>initial</em> and <em>residual</em>
              risk scores. Click any cell to update likelihood & impact, and follow the arrow to see how your
              mitigation strategy has moved the risk. Hover over risks for detailed tooltips.
            </li>
            <li>
              <strong>Risk Register:</strong> A sortable, filterable table showing every risk together with
              categories, actions, owners, deadlines, and dynamically calculated scores. Click any cell to edit
              values in-place with enhanced tooltips for guidance.
            </li>
            <li>
              <strong>Kanban Board:</strong> Drag-and-drop cards to quickly change a risk's status, owner,
              category, or action — simply choose the grouping you want and move the card. Group by Status,
              Category, Action, or Owner.
            </li>
            <li>
              <strong>Sidebar Filters:</strong> Instantly narrow down the register, matrix, and kanban by
              status, category, action, owner, or due-date criteria. Toggle sidebar visibility to maximize
              workspace.
            </li>
            <li>
              <strong>Custom Lists:</strong> Add, rename, or remove categories, statuses, and actions to match
              your organisation's terminology. Access via the sidebar management buttons.
            </li>
            <li>
              <strong>Column Customization:</strong> Customize your risk register table by showing/hiding columns,
              reordering them via drag-and-drop, and adjusting column types (text, textarea, select, date, number).
              Click the settings icon to access column management.
            </li>
            <li>
              <strong>Full View Mode:</strong> Toggle between normal view and full view to maximize the risk
              register workspace. Perfect for detailed data entry and analysis sessions.
            </li>
            <li>
              <strong>Enhanced Inline Editing:</strong> Click any table cell to edit values in-place with
              contextual tooltips providing guidance. Press Enter or click outside to save changes.
            </li>
            <li>
              <strong>Smart Exports:</strong> Generate polished PDF reports (including the matrix on page one) or
              spreadsheet files that respect your current column configuration and filters.
            </li>
            <li>
              <strong>Sample Data:</strong> Quickly load 3, 8, or 20 example risks to explore the tool before
              loading your own data. Sample data includes realistic scenarios with proper risk scoring.
            </li>
            <li>
              <strong>Data Import/Export:</strong> Import data from Excel files or download templates for
              bulk data entry. Export current filtered view or complete dataset.
            </li>
            <li>
              <strong>Responsive Design:</strong> Works seamlessly on desktop, tablet, and mobile devices
              with optimized layouts for different screen sizes.
            </li>
          </ul>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
            <h3 className="font-semibold text-blue-800 mb-2">💡 Quick Tips</h3>
            <ul className="text-blue-700 text-xs space-y-1">
              <li>• Use the <strong>Full View</strong> button to maximize workspace for data entry</li>
              <li>• <strong>Hover over any field</strong> for detailed tooltips and guidance</li>
              <li>• <strong>Drag column headers</strong> to reorder your table layout</li>
              <li>• <strong>Click the settings icon</strong> to customize which columns are visible</li>
              <li>• <strong>Use filters</strong> to focus on specific risk categories or statuses</li>
              <li>• <strong>Export filtered views</strong> to share specific risk subsets with stakeholders</li>
            </ul>
          </div>
          
          <p>
            Need more help? Feel free to reach out to the development team or check the project documentation
            for advanced tips and troubleshooting.
          </p>
        </div>
      </div>
    </div>
  );
} 